import Icon from '@components/v2/ui/Icon/Icon';
import {
  Activity,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Ban,
  Check,
  CircleOff,
  ClipboardList,
  CloudDownload,
  DollarSign,
  Download,
  FileText,
  History,
  Plus,
  Receipt,
  RefreshCw,
  Share,
  Split,
  Star,
  X,
} from 'lucide-react';
import AppEnvironment from '../../../components/enums/appEnvironment.enum';
import LangEnum from '../../../lang/LangEnum';
import { OrderListTestIds } from '../../../utils/testIds/OrderList.testIds';
import { OfflineStatus } from '../../offline/domain/offlineOrder/offlineOrder.types';
import { StatusType } from '../order.types';
import { canForceReverseSyncOrder, canForceSyncOrder } from '../sync/forceSyncOrder.utils';
import { ActionsType, Role } from './types/actions.types';
import { ActionEnum } from './types/orderActions.types';
import { allRoles, brandRoles } from './utils/actions.utils';
import {
  toForceReverseSyncOrderParams,
  toForceSyncOrderParams,
} from './utils/forceSyncOrderAction.utils';

export type SummaryAction =
  | ActionEnum.SYNC
  | ActionEnum.VIEW_ORDER_ACTIVITIES
  | ActionEnum.CHECK_ORDER_INTEGRATION
  | ActionEnum.FORCE_SYNC_ORDER
  | ActionEnum.FORCE_REVERSE_SYNC_ORDER
  | ActionEnum.FINISH_TO_IN_REVISION
  | ActionEnum.FINISH_TO_ON_APPROVAL
  | ActionEnum.FINISH_TO_APPROVED
  | ActionEnum.MOVE_TO_SENT
  | ActionEnum.BACK_TO_DRAFT
  | ActionEnum.CONFIRM_BACK_TO_DRAFT
  | ActionEnum.BACK_TO_IN_REVISION
  | ActionEnum.BACK_TO_ON_APPROVAL
  | ActionEnum.BACK_TO_APPROVED
  | ActionEnum.MOVE_TO_CANCELED
  | ActionEnum.ARCHIVE_ORDER
  | ActionEnum.UNARCHIVE_ORDER
  | ActionEnum.OPEN_ORDER_PAYMENT_MODAL
  | ActionEnum.OPEN_ORDER_PAYMENT_HISTORY_MODAL
  | ActionEnum.OPEN_ORDER_REFUND_PAYMENT_MODAL
  | ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL
  | ActionEnum.AUTHORIZE_ORDER_PAYMENT
  | ActionEnum.VERUP_EXPORT_CSV
  | ActionEnum.EXPORT_ORDER
  | ActionEnum.EXPORT_MULTIPLE_ORDERS
  | ActionEnum.OPEN_CONTRACT_MODAL
  | ActionEnum.VIEW_CONTRACTS
  | ActionEnum.DOWNLOAD_MEDIAS
  | ActionEnum.SPLIT_ORDER
  | ActionEnum.COPY_AS_DRAFT
  | ActionEnum.COPY_AS_SUGGESTED
  | ActionEnum.SHARE_ORDER
  | ActionEnum.OPEN_UPDATE_ORDER_MODAL
  | ActionEnum.SYNC_OFFLINE_ORDER
  | ActionEnum.ACCEPT_SUGGESTION
  | ActionEnum.MOVE_TO_REJECTED;

export const ACTION_TO_ORDER_STATUS_MAP: Record<string, StatusType> = {
  [ActionEnum.MOVE_TO_SENT]: StatusType.SENT,
  [ActionEnum.BACK_TO_DRAFT]: StatusType.DRAFT,
  [ActionEnum.MOVE_TO_CANCELED]: StatusType.CANCELED,
  [ActionEnum.BACK_TO_APPROVED]: StatusType.APPROVED,
  [ActionEnum.MOVE_TO_REJECTED]: StatusType.REJECTED,
  [ActionEnum.FINISH_TO_APPROVED]: StatusType.APPROVED,
  [ActionEnum.BACK_TO_IN_REVISION]: StatusType.IN_REVISION,
  [ActionEnum.BACK_TO_ON_APPROVAL]: StatusType.ON_APPROVAL,
  [ActionEnum.FINISH_TO_ON_APPROVAL]: StatusType.ON_APPROVAL,
  [ActionEnum.FINISH_TO_IN_REVISION]: StatusType.IN_REVISION,
};

export const allSummaryActions: { [key in SummaryAction]: ActionsType } = {
  [ActionEnum.SYNC]: {
    icon: <Icon icon={RefreshCw} size="md" />,
    label: LangEnum.ORDER_SUMMARY_MORE_ACTIONS_SYNC,
    value: ActionEnum.SYNC,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: [Role.ADMIN_MARCA],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.IN_REVISION]: [Role.ADMIN_MARCA],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.CHECK_ORDER_INTEGRATION]: {
    icon: <Icon icon={History} size="md" />,
    label: LangEnum.ORDER_SUMMARY_MORE_ACTIONS_CHECK_INTEGRATION,
    value: ActionEnum.CHECK_ORDER_INTEGRATION,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: brandRoles,
        [StatusType.IN_REVISION]: brandRoles,
        [StatusType.ON_APPROVAL]: brandRoles,
        [StatusType.APPROVED]: brandRoles,
        [StatusType.SENT]: brandRoles,
        [StatusType.CANCELED]: brandRoles,
        [StatusType.ABANDONED]: brandRoles,
        [StatusType.REJECTED]: brandRoles,
        [StatusType.SUGGESTED]: [],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.VIEW_ORDER_ACTIVITIES]: {
    icon: <Icon icon={Activity} size="md" />,
    label: LangEnum.GLOBAL_VIEW_ACTIVITIES,
    value: ActionEnum.VIEW_ORDER_ACTIVITIES,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
        [StatusType.CANCELED]: allRoles,
        [StatusType.ABANDONED]: allRoles,
        [StatusType.REJECTED]: allRoles,
        [StatusType.SUGGESTED]: allRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND, AppEnvironment.STORE],
    },
  },
  [ActionEnum.FORCE_SYNC_ORDER]: {
    icon: <Icon icon={RefreshCw} size="md" />,
    label: LangEnum.SYNC_HISTORY_FORCE_SYNC_LABEL,
    value: ActionEnum.FORCE_SYNC_ORDER,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: [Role.ADMIN_MARCA],
        [StatusType.IN_REVISION]: [Role.ADMIN_MARCA],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
        [StatusType.SENT]: [Role.ADMIN_MARCA],
        [StatusType.CANCELED]: [Role.ADMIN_MARCA],
        [StatusType.ABANDONED]: [Role.ADMIN_MARCA],
        [StatusType.REJECTED]: [Role.ADMIN_MARCA],
        [StatusType.SUGGESTED]: [Role.ADMIN_MARCA],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
      isAllowed: (params) => canForceSyncOrder(toForceSyncOrderParams(params)),
    },
  },
  [ActionEnum.FORCE_REVERSE_SYNC_ORDER]: {
    icon: <Icon icon={ArrowRightLeft} size="md" />,
    label: LangEnum.ORDER_SYNC_HISTORY_FORCE_REVERSE_SYNC_LABEL,
    value: ActionEnum.FORCE_REVERSE_SYNC_ORDER,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: [Role.ADMIN_MARCA],
        [StatusType.IN_REVISION]: [Role.ADMIN_MARCA],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
        [StatusType.SENT]: [Role.ADMIN_MARCA],
        [StatusType.CANCELED]: [Role.ADMIN_MARCA],
        [StatusType.ABANDONED]: [Role.ADMIN_MARCA],
        [StatusType.REJECTED]: [Role.ADMIN_MARCA],
        [StatusType.SUGGESTED]: [Role.ADMIN_MARCA],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
      isAllowed: (params) => canForceReverseSyncOrder(toForceReverseSyncOrderParams(params)),
    },
  },
  [ActionEnum.FINISH_TO_IN_REVISION]: {
    icon: <Icon icon={ArrowRight} size="md" />,
    label: LangEnum.ORDER_SUMMARY_FINISH_ORDER_ACTION_LABEL,
    value: ActionEnum.FINISH_TO_IN_REVISION,
    testId: OrderListTestIds.FINALIZE_ORDER_ACTION,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: [Role.ADMIN_LOJA, Role.COMPRADOR],
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.FINISH_TO_ON_APPROVAL]: {
    icon: <Icon icon={ArrowRight} size="md" />,
    label: LangEnum.ORDER_SUMMARY_FINISH_ORDER_ACTION_LABEL,
    value: ActionEnum.FINISH_TO_ON_APPROVAL,
    testId: OrderListTestIds.FINALIZE_ORDER_ACTION,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: brandRoles,
        [StatusType.IN_REVISION]: brandRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.FINISH_TO_APPROVED]: {
    icon: <Icon icon={Check} size="md" />,
    label: LangEnum.ORDER_SUMMARY_FINISH_TO_APPROVED_ACTION_LABEL,
    value: ActionEnum.FINISH_TO_APPROVED,
    testId: OrderListTestIds.FINALIZE_ORDER_ACTION,
    conditions: {
      statusRole: {
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
      isAllowed: ({ triggerIntegrationConfig }) =>
        triggerIntegrationConfig !== StatusType.ON_APPROVAL,
    },
  },
  [ActionEnum.MOVE_TO_REJECTED]: {
    icon: <Icon icon={X} size="md" />,
    label: LangEnum.GLOBAL_REJECT_ORDER,
    value: ActionEnum.MOVE_TO_REJECTED,
    conditions: {
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
      statusRole: {
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
      },
      statusAbility: {
        [StatusType.APPROVED]: ['move-order-status-to-rejected'],
      },
    },
  },
  [ActionEnum.COPY_AS_SUGGESTED]: {
    icon: <Icon icon={Star} size="md" />,
    label: LangEnum.GLOBAL_COPY_AS_SUGGESTED,
    value: ActionEnum.COPY_AS_SUGGESTED,
    conditions: {
      statusRole: {
        [StatusType.ABANDONED]: brandRoles,
        [StatusType.CANCELED]: brandRoles,
        [StatusType.DRAFT]: brandRoles,
        [StatusType.IN_REVISION]: brandRoles,
        [StatusType.ON_APPROVAL]: brandRoles,
        [StatusType.APPROVED]: brandRoles,
        [StatusType.SENT]: brandRoles,
        [StatusType.REJECTED]: brandRoles,
        [StatusType.SUGGESTED]: brandRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
      isAllowed: ({ role, sellerCanCreateOrChangeSuggestedOrder }) =>
        role !== Role.VENDEDOR || !!sellerCanCreateOrChangeSuggestedOrder,
    },
    testId: OrderListTestIds.COPY_AS_SUGGESTED_ACTION,
  },
  [ActionEnum.ACCEPT_SUGGESTION]: {
    icon: <Icon icon={Check} size="md" />,
    label: LangEnum.SUGGESTED_ORDER_ACCEPT_SUGGESTION,
    value: ActionEnum.ACCEPT_SUGGESTION,
    conditions: {
      statusRole: {
        [StatusType.SUGGESTED]: brandRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.MOVE_TO_SENT]: {
    icon: <Icon icon={ArrowRight} size="md" />,
    label: LangEnum.ORDER_SUMMARY_MOVE_TO_SENT_ACTION_LABEL,
    value: ActionEnum.MOVE_TO_SENT,
    conditions: {
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
      statusRole: {
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
      },
      statusAbility: {
        [StatusType.APPROVED]: ['move-order-status-to-sent'],
      },
    },
  },
  [ActionEnum.BACK_TO_DRAFT]: {
    icon: <Icon icon={ArrowLeft} size="md" />,
    label: LangEnum.ORDER_SUMMARY_BACK_TO_DRAFT_ACTION_LABEL,
    value: ActionEnum.BACK_TO_DRAFT,
    conditions: {
      statusRole: {
        [StatusType.IN_REVISION]: brandRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.CONFIRM_BACK_TO_DRAFT]: {
    icon: <Icon icon={ArrowLeft} size="md" />,
    label: LangEnum.ORDER_SUMMARY_BACK_TO_DRAFT_ACTION_LABEL,
    value: ActionEnum.CONFIRM_BACK_TO_DRAFT,
    conditions: {
      statusRole: {
        [StatusType.CANCELED]: brandRoles,
        [StatusType.ABANDONED]: allRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.BACK_TO_IN_REVISION]: {
    icon: <Icon icon={ArrowLeft} size="md" />,
    label: LangEnum.ORDER_SUMMARY_BACK_TO_REVISION_ACTION_LABEL,
    value: ActionEnum.BACK_TO_IN_REVISION,
    conditions: {
      statusRole: {
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.BACK_TO_ON_APPROVAL]: {
    icon: <Icon icon={ArrowLeft} size="md" />,
    label: LangEnum.ORDER_SUMMARY_BACK_TO_APPROVAL_ACTION_LABEL,
    value: ActionEnum.BACK_TO_ON_APPROVAL,
    conditions: {
      statusRole: {
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.BACK_TO_APPROVED]: {
    icon: <Icon icon={ArrowLeft} size="md" />,
    label: LangEnum.ORDER_SUMMARY_BACK_TO_APPROVED_ACTION_LABEL,
    value: ActionEnum.BACK_TO_APPROVED,
    conditions: {
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
      statusRole: {
        [StatusType.SENT]: [Role.ADMIN_MARCA],
        [StatusType.REJECTED]: [Role.ADMIN_MARCA],
      },
      statusAbility: {
        [StatusType.REJECTED]: ['move-order-status-to-approved'],
      },
    },
  },
  [ActionEnum.MOVE_TO_CANCELED]: {
    icon: <Icon icon={Ban} size="md" />,
    label: LangEnum.ORDER_SUMMARY_CANCEL_ORDER_ACTION_LABEL,
    value: ActionEnum.MOVE_TO_CANCELED,
    testId: OrderListTestIds.CANCEL_ORDER_ACTION,
    conditions: {
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
      statusRole: {
        [StatusType.ABANDONED]: allRoles,
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: brandRoles,
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
        [StatusType.SENT]: [Role.ADMIN_MARCA],
        [StatusType.REJECTED]: [Role.ADMIN_MARCA],
      },
      statusAbility: {
        [StatusType.APPROVED]: ['move-order-status-to-canceled'],
        [StatusType.REJECTED]: ['move-order-status-to-canceled'],
      },
    },
  },
  [ActionEnum.ARCHIVE_ORDER]: {
    label: LangEnum.ORDER_SUMMARY_ARCHIVE_ORDER_ACTION_LABEL,
    icon: <Icon icon={Archive} size="md" />,
    value: ActionEnum.ARCHIVE_ORDER,
    conditions: {
      statusRole: {
        [StatusType.SUGGESTED]: brandRoles,
        [StatusType.CANCELED]: brandRoles,
        [StatusType.ABANDONED]: brandRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.UNARCHIVE_ORDER]: {
    label: LangEnum.ORDER_SUMMARY_UNARCHIVE_ORDER_ACTION_LABEL,
    icon: <Icon icon={ArchiveRestore} size="md" />,
    value: ActionEnum.UNARCHIVE_ORDER,
    conditions: {
      statusRole: {
        [StatusType.SUGGESTED]: brandRoles,
        [StatusType.CANCELED]: brandRoles,
        [StatusType.ABANDONED]: brandRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.OPEN_ORDER_PAYMENT_MODAL]: {
    label: LangEnum.ORDER_PAGE_MAKE_PAYMENT,
    icon: <Icon icon={DollarSign} size="md" />,
    value: ActionEnum.OPEN_ORDER_PAYMENT_MODAL,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.OPEN_ORDER_PAYMENT_HISTORY_MODAL]: {
    label: LangEnum.ORDER_PAGE_SEE_PAYMENTS,
    icon: <Icon icon={Receipt} size="md" />,
    value: ActionEnum.OPEN_ORDER_PAYMENT_HISTORY_MODAL,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.OPEN_ORDER_REFUND_PAYMENT_MODAL]: {
    label: LangEnum.PAYMENT_LINK_REFUND_BUTTON_LABEL,
    icon: <Icon icon={ArrowRightLeft} size="md" />,
    value: ActionEnum.OPEN_ORDER_REFUND_PAYMENT_MODAL,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: [],
        [StatusType.IN_REVISION]: [],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
        [StatusType.SENT]: [Role.ADMIN_MARCA],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL]: {
    label: LangEnum.PAYMENT_LINK_CANCEL_PAYMENT_LINK_LABEL,
    icon: <Icon icon={CircleOff} size="md" />,
    value: ActionEnum.OPEN_CANCEL_PAYMENT_LINK_MODAL,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: [],
        [StatusType.IN_REVISION]: [],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
        [StatusType.SENT]: [Role.ADMIN_MARCA],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.AUTHORIZE_ORDER_PAYMENT]: {
    label: LangEnum.ORDER_PAGE_AUTHORIZE_PAYMENT,
    icon: <Icon icon={DollarSign} size="md" />,
    value: ActionEnum.AUTHORIZE_ORDER_PAYMENT,
    conditions: {
      statusRole: {
        [StatusType.IN_REVISION]: [Role.ADMIN_MARCA],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.APPROVED]: [Role.ADMIN_MARCA],
        [StatusType.SENT]: [Role.ADMIN_MARCA],
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.VERUP_EXPORT_CSV]: {
    label: LangEnum.ORDER_SUMMARY_VERUP_EXPORT_CSV_ACTION_LABEL,
    icon: <Icon icon={CloudDownload} size="md" />,
    value: ActionEnum.VERUP_EXPORT_CSV,
    conditions: {
      statusRole: {},
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.EXPORT_ORDER]: {
    label: LangEnum.GLOBAL_EXPORT,
    icon: <Icon icon={CloudDownload} size="md" />,
    value: ActionEnum.EXPORT_ORDER,
    conditions: {
      statusAbility: {
        [StatusType.ABANDONED]: ['export-order-pdf'],
        [StatusType.CANCELED]: ['export-order-pdf'],
        [StatusType.DRAFT]: ['export-order-pdf'],
        [StatusType.IN_REVISION]: ['export-order-pdf'],
        [StatusType.ON_APPROVAL]: ['export-order-pdf'],
        [StatusType.APPROVED]: ['export-order-pdf'],
        [StatusType.SENT]: ['export-order-pdf'],
        [StatusType.REJECTED]: ['export-order-pdf'],
        [StatusType.SUGGESTED]: ['export-order-pdf'],
        [OfflineStatus.OFFLINE]: ['offline-export-order-pdf'],
        [OfflineStatus.ERROR_SYNCING]: ['offline-export-order-pdf'],
      },
      statusRole: {
        [StatusType.ABANDONED]: allRoles,
        [StatusType.CANCELED]: allRoles,
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
        [StatusType.REJECTED]: allRoles,
        [StatusType.SUGGESTED]: allRoles,
        [OfflineStatus.OFFLINE]: brandRoles,
        [OfflineStatus.ERROR_SYNCING]: brandRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND, AppEnvironment.STORE],
    },
  },
  [ActionEnum.EXPORT_MULTIPLE_ORDERS]: {
    label: LangEnum.GLOBAL_EXPORT,
    icon: <Icon icon={CloudDownload} size="md" />,
    value: ActionEnum.EXPORT_MULTIPLE_ORDERS,
    conditions: {
      statusAbility: {
        [StatusType.ABANDONED]: ['export-order-pdf'],
        [StatusType.CANCELED]: ['export-order-pdf'],
        [StatusType.DRAFT]: ['export-order-pdf'],
        [StatusType.IN_REVISION]: ['export-order-pdf'],
        [StatusType.ON_APPROVAL]: ['export-order-pdf'],
        [StatusType.APPROVED]: ['export-order-pdf'],
        [StatusType.SENT]: ['export-order-pdf'],
        [StatusType.REJECTED]: ['export-order-pdf'],
        [StatusType.SUGGESTED]: ['export-order-pdf'],
        [OfflineStatus.OFFLINE]: ['offline-export-order-pdf'],
        [OfflineStatus.ERROR_SYNCING]: ['offline-export-order-pdf'],
      },
      statusRole: {
        [StatusType.ABANDONED]: allRoles,
        [StatusType.CANCELED]: allRoles,
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
        [StatusType.REJECTED]: allRoles,
        [StatusType.SUGGESTED]: allRoles,
        [OfflineStatus.OFFLINE]: brandRoles,
        [OfflineStatus.ERROR_SYNCING]: brandRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.OPEN_CONTRACT_MODAL]: {
    label: LangEnum.ORDER_SUMMARY_OPEN_CONTRACT_MODAL_ACTION_LABEL,
    icon: <Icon icon={ClipboardList} size="md" />,
    value: ActionEnum.OPEN_CONTRACT_MODAL,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.VIEW_CONTRACTS]: {
    label: LangEnum.ORDER_SUMMARY_VIEW_CONTRACTS_ACTION_LABEL,
    icon: <Icon icon={FileText} size="md" />,
    value: ActionEnum.VIEW_CONTRACTS,
    conditions: {
      statusRole: {
        [StatusType.CANCELED]: allRoles,
        [StatusType.REJECTED]: allRoles,
        [StatusType.ABANDONED]: allRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.COPY_AS_DRAFT]: {
    icon: <Icon icon={Plus} size="md" />,
    label: LangEnum.GLOBAL_COPY_AS_DRAFT,
    value: ActionEnum.COPY_AS_DRAFT,
    conditions: {
      statusRole: {
        [StatusType.SUGGESTED]: allRoles,
        [StatusType.ABANDONED]: allRoles,
        [StatusType.CANCELED]: allRoles,
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
        [StatusType.REJECTED]: allRoles,
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
    testId: OrderListTestIds.COPY_AS_DRAFT_ACTION,
  },
  [ActionEnum.DOWNLOAD_MEDIAS]: {
    icon: <Icon icon={Download} size="md" />,
    label: LangEnum.ORDER_SUMMARY_DOWNLOAD_MEDIAS_ACTION_LABEL,
    value: ActionEnum.DOWNLOAD_MEDIAS,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: allRoles,
        [StatusType.IN_REVISION]: allRoles,
        [StatusType.ON_APPROVAL]: allRoles,
        [StatusType.APPROVED]: allRoles,
        [StatusType.SENT]: allRoles,
        [StatusType.CANCELED]: allRoles,
        [StatusType.ABANDONED]: allRoles,
        [StatusType.REJECTED]: allRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND, AppEnvironment.STORE],
    },
  },
  [ActionEnum.SPLIT_ORDER]: {
    icon: <Icon icon={Split} size="md" />,
    label: LangEnum.ORDER_SUMMARY_SPLIT_ORDER_ACTION_LABEL,
    value: ActionEnum.SPLIT_ORDER,
    conditions: {
      statusRole: {
        [StatusType.IN_REVISION]: [Role.ADMIN_MARCA],
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.CANCELED]: [Role.ADMIN_MARCA],
        [StatusType.ABANDONED]: [Role.ADMIN_MARCA],
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.OPEN_UPDATE_ORDER_MODAL]: {
    icon: <Icon icon={RefreshCw} size="md" />,
    label: LangEnum.GLOBAL_UPDATE_ORDER,
    value: ActionEnum.OPEN_UPDATE_ORDER_MODAL,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: brandRoles,
        [StatusType.IN_REVISION]: brandRoles,
        [StatusType.ON_APPROVAL]: [Role.ADMIN_MARCA],
        [StatusType.SUGGESTED]: [Role.ADMIN_MARCA],
      },
      massAction: true,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.SHARE_ORDER]: {
    icon: <Icon icon={Share} size="md" />,
    label: LangEnum.GLOBAL_SHARE_ORDER,
    value: ActionEnum.SHARE_ORDER,
    conditions: {
      statusRole: {
        [StatusType.DRAFT]: brandRoles,
        [StatusType.IN_REVISION]: brandRoles,
        [StatusType.ON_APPROVAL]: brandRoles,
        [StatusType.APPROVED]: brandRoles,
        [StatusType.SENT]: brandRoles,
        [StatusType.CANCELED]: brandRoles,
        [StatusType.ABANDONED]: brandRoles,
        [StatusType.REJECTED]: brandRoles,
        [StatusType.SUGGESTED]: brandRoles,
      },
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
    },
  },
  [ActionEnum.SYNC_OFFLINE_ORDER]: {
    icon: <Icon icon={RefreshCw} size="md" />,
    label: LangEnum.GLOBAL_SYNC_ORDER,
    value: ActionEnum.SYNC_OFFLINE_ORDER,
    testId: OrderListTestIds.SYNC_OFFLINE_ORDER_ACTION,
    conditions: {
      massAction: false,
      appEnvironment: [AppEnvironment.BRAND],
      statusRole: {
        [OfflineStatus.OFFLINE]: brandRoles,
        [OfflineStatus.ERROR_SYNCING]: brandRoles,
      },
    },
  },
};

export const orderSummaryAllActions: ActionsType[] = [
  allSummaryActions.OPEN_ORDER_PAYMENT_MODAL,
  allSummaryActions.OPEN_ORDER_PAYMENT_HISTORY_MODAL,
  allSummaryActions.AUTHORIZE_ORDER_PAYMENT,
  allSummaryActions.OPEN_CANCEL_PAYMENT_LINK_MODAL,
  allSummaryActions.OPEN_ORDER_REFUND_PAYMENT_MODAL,
  allSummaryActions.SYNC,
  allSummaryActions.VIEW_ORDER_ACTIVITIES,
  allSummaryActions.CHECK_ORDER_INTEGRATION,
  allSummaryActions.FORCE_SYNC_ORDER,
  allSummaryActions.FORCE_REVERSE_SYNC_ORDER,
  allSummaryActions.FINISH_TO_IN_REVISION,
  allSummaryActions.FINISH_TO_ON_APPROVAL,
  allSummaryActions.FINISH_TO_APPROVED,
  allSummaryActions.MOVE_TO_SENT,
  allSummaryActions.BACK_TO_DRAFT,
  allSummaryActions.CONFIRM_BACK_TO_DRAFT,
  allSummaryActions.BACK_TO_IN_REVISION,
  allSummaryActions.BACK_TO_ON_APPROVAL,
  allSummaryActions.BACK_TO_APPROVED,
  allSummaryActions.MOVE_TO_REJECTED,
  allSummaryActions.MOVE_TO_CANCELED,
  allSummaryActions.ARCHIVE_ORDER,
  allSummaryActions.UNARCHIVE_ORDER,
  allSummaryActions.VERUP_EXPORT_CSV,
  allSummaryActions.EXPORT_ORDER,
  allSummaryActions.EXPORT_MULTIPLE_ORDERS,
  allSummaryActions.OPEN_CONTRACT_MODAL,
  allSummaryActions.VIEW_CONTRACTS,
  allSummaryActions.DOWNLOAD_MEDIAS,
  allSummaryActions.OPEN_UPDATE_ORDER_MODAL,
  allSummaryActions.SPLIT_ORDER,
  allSummaryActions.COPY_AS_DRAFT,
  allSummaryActions.COPY_AS_SUGGESTED,
  allSummaryActions.ACCEPT_SUGGESTION,
  allSummaryActions.SHARE_ORDER,
  allSummaryActions.SYNC_OFFLINE_ORDER,
];
